'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { Button } from '@components/Form';
import { fetchApi } from '@utils/api/client';
import { useUserStore } from '@utils/user';

interface DraftModeBarProps {
  slug?: string;
}

const REVALIDATE_RESULT_LABELS: Record<string, string> = {
  purged: 'Article removed from index (deleted in WordPress)',
  published: 'Article still published - caches refreshed',
  skipped: 'Local caches refreshed (gateway check skipped)',
  error: 'Gateway error - local caches refreshed',
};

export default function DraftModeBar({ slug }: DraftModeBarProps) {
  const [isDraftMode, setIsDraftMode] = useState(true);
  const [isImpersonatingPremium, setIsImpersonatingPremium] = useState(false);
  const [isDisablingDraftMode, setIsDisablingDraftMode] = useState(false);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [revalidateResult, setRevalidateResult] = useState<string | null>(null);
  const userStore = useUserStore();
  const router = useRouter();

  const toggleImpersonatingPremium = () => {
    userStore.setPremiumOverride(!isImpersonatingPremium);
    setIsImpersonatingPremium(!isImpersonatingPremium);
  };

  const revalidateArticleStatus = async () => {
    if (!slug || isRevalidating) {
      return;
    }

    setIsRevalidating(true);
    setRevalidateResult(null);

    try {
      const res = await fetchApi<{ gatewayStatus?: string }>(`/posts/${slug}/revalidate-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: window.location.pathname }),
        onFailure: 'continue',
      });

      if (res.status !== 200) {
        setRevalidateResult('Revalidation failed');
      } else {
        const gatewayStatus = res.data?.gatewayStatus ?? '';
        setRevalidateResult(REVALIDATE_RESULT_LABELS[gatewayStatus] || 'Caches refreshed');
        router.refresh();
      }
    } catch {
      setRevalidateResult('Revalidation failed');
    } finally {
      setIsRevalidating(false);
    }
  };

  const exitDraftMode = async () => {
    if (isDisablingDraftMode) {
      return;
    }

    setIsDisablingDraftMode(true);
    userStore.disablePremiumOverride();

    await fetchApi('/posts/disable-draft');

    setIsDraftMode(false);
  };

  if (!isDraftMode) return null;

  return (
    <div className="fixed bottom-6 z-[100] flex w-full items-center justify-center lg:bottom-40">
      <div className="max-w-[480px] flex-grow rounded-lg border border-gray-300 bg-white px-4 py-2 shadow-lg lg:px-6 lg:py-3">
        <h6 className="mb-3 text-center text-lg font-bold text-premium-900">
          Draft Mode
        </h6>
        <div className="flex items-center justify-between gap-6 whitespace-nowrap">
          <div className="flex max-w-48 flex-1 items-center">
            <span className="mr-2">Viewing as:</span>
            <Button type="button" onClick={toggleImpersonatingPremium}>
              {isImpersonatingPremium ? 'Premium' : 'Free'}
            </Button>
          </div>
          <Button
            type="button"
            className="max-w-48 flex-1"
            loading={isDisablingDraftMode}
            disabled={isDisablingDraftMode}
            onClick={exitDraftMode}
          >
            Exit Draft
          </Button>
        </div>
        {slug && (
          <div className="mt-3 flex flex-col items-center gap-1">
            <Button
              type="button"
              className="w-full"
              loading={isRevalidating}
              disabled={isRevalidating}
              onClick={revalidateArticleStatus}
            >
              Revalidate article status
            </Button>
            {revalidateResult && (
              <span className="text-center text-sm text-gray-600">{revalidateResult}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
